/**
 * **URL: none of its own.** The layout for `/g/$gardenToken` and everything under it —
 * `route.tsx` names the segment's layout, so this renders around the index route beside it
 * and around all four tabs.
 *
 * Two responsibilities, and they are the two only a route file at this segment can hold:
 *
 * 1. **The `?v=` search schema.** `validateSearch` below is the only guard on the detail
 *    ref: the router JSON-parses search values, so without it `?v=[1,2]` would hand the
 *    detail stack an array where the type says `string`. It also defines the route id
 *    `DetailStack` binds to (`useSearch({ from: "/_app/g/$gardenToken" })`), and
 *    `DetailStack` treats its own `isDetailRef` check as a second line of defence
 *    *because* this ran first.
 * 2. **`GardenEntriesProvider`.** The journal's paginated query must outlive a tab switch —
 *    the tabs are routes, so Plants unmounts Journal — but must not outlive the garden.
 *    This segment is exactly that lifetime. Hoisting it to `_app.tsx` would open a
 *    subscription for an arbitrary garden on `/`.
 *
 * **Not here: anything about the destinations.** They live in `GardenNav`, placed by
 * `AppChrome` in `_app.tsx`, so that at regular width they and the top bar become one
 * sidebar (`docs/responsive-layout.md` §4.2). They are links, so the router is the
 * navigation state and this segment holds none of it.
 */

import { createFileRoute, Outlet } from '@tanstack/react-router';

import { GardenEntriesProvider } from '../../components/GardenEntriesProvider';
import { isDetailRef } from '../../lib/detailItem';


type GardenSearch = {
  v?: string
}

export const Route = createFileRoute('/_app/g/$gardenToken')({
  // `v` comes from the url, so only its *shape* can be checked here — whether
  // it names a row that exists, and one this user may see, is the server's
  // answer and arrives later as a query result (see useDetailItem). Without
  // this the declared `v?: string` is a lie: the router JSON-parses search
  // values, so `?v=[1,2]` would hand the detail stack an array.
  // Anything else in the query string is passed through untouched.
  validateSearch: (search: Record<string, unknown>): GardenSearch => ({
    ...search,
    v: typeof search.v === "string" && isDetailRef(search.v) ? search.v : undefined,
  }),
  component: function GardenComponent() {
    return (
      <GardenEntriesProvider>
        <Outlet />
      </GardenEntriesProvider>
    );
  },
})
